import { useMemo } from "react";

import useURLParams from "hooks/useURLParams";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import { fuzzySearch } from "utils/fuzzySearch";
import SearchInput from "components/SearchInput";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemTitle from "components/ListEntitiesItem/Title";
import Typography from "ds/components/Typography";
import EmptyState from "ds/components/EmptyState";
import Box from "ds/components/Box";
import Code from "ds/components/Code";
import { SortableTableColumn } from "components/SortableTable";
import { SearchQueryOrderDirection } from "types/generated";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import PageInfoHeader from "ds/components/PageInfoHeader";

import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";
import { FILTERS_ORDER_SETTINGS_KEY_VERSION_DEPENDENCIES } from "./constants";
import CollapsibleTable from "../components/CollapsibleTable";

const columns: SortableTableColumn[] = [
  {
    value: "id",
    label: "Name",
  },
];

type ModuleVersionDependenciesProps = {
  providerDependencies: Array<{
    name: string;
    version: string;
    source: string;
  }>;
  moduleDependencies: string[];
};

const ModuleVersionDependencies = ({
  providerDependencies,
  moduleDependencies,
}: ModuleVersionDependenciesProps) => {
  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const [filteredProviderDependencies, filteredModuleDependencies] = useMemo(() => {
    let filteredProviderDependencies = providerDependencies.map((dependency) => ({
      ...dependency,
      id: dependency.name,
    }));

    let filteredModuleDependencies = moduleDependencies.map((dependency) => ({
      id: dependency,
    }));

    if (searchQuery) {
      const query = searchQuery.trim();

      filteredProviderDependencies = fuzzySearch(filteredProviderDependencies, query, {
        keys: ["name", "source", "version"],
        scoreThreshold: -1000,
      });

      filteredModuleDependencies = fuzzySearch(filteredModuleDependencies, query, {
        keys: ["id"],
        scoreThreshold: -1000,
      });
    }

    return [filteredProviderDependencies, filteredModuleDependencies];
  }, [providerDependencies, moduleDependencies, searchQuery]);

  return (
    <>
      <PageInfoHeader title="Dependencies">
        <SearchInput
          placeholder="Search by name and version..."
          filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_DEPENDENCIES}
        />
      </PageInfoHeader>
      <ModuleVersionScrollableWrapper gap="x-large">
        {filteredModuleDependencies.length === 0 && filteredProviderDependencies.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={EmptystateSearchNoResultsColored}
            caption={
              searchQuery
                ? "No dependencies match your search query."
                : "This module has no dependencies."
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {filteredModuleDependencies.length > 0 && (
          <CollapsibleTable
            title="Module dependencies"
            items={filteredModuleDependencies}
            columns={columns}
            columnOrder="1fr"
            renderItems={(sorted) =>
              sorted.map((item, index) => (
                <ListEntitiesItem
                  key={item.id}
                  gap="small"
                  noSeparator={index === filteredModuleDependencies.length - 1}
                  role="row"
                >
                  <ListEntitiesItemTitle role="rowheader" title={item.id} titleVariant="p-body2" />
                </ListEntitiesItem>
              ))
            }
            initialSortBy="id"
            initialDirection={SearchQueryOrderDirection.Desc}
          />
        )}
        {filteredProviderDependencies.length > 0 && (
          <CollapsibleTable
            title="Provider dependencies"
            items={filteredProviderDependencies}
            columns={columns}
            columnOrder="1fr"
            renderItems={(sorted) =>
              sorted.map((item, index) => (
                <ListEntitiesItem
                  key={item.id}
                  noSeparator={index === filteredProviderDependencies.length - 1}
                  role="row"
                >
                  <Box role="rowheader" direction="column" gap="small">
                    <Box gap="medium" align="center">
                      <ListEntitiesItemTitle title={item.id} titleVariant="p-body2" />
                      {item.version && <Code>{item.version}</Code>}
                    </Box>

                    {item.source && (
                      <Typography tag="p" variant="p-body3" color="secondary">
                        {item.source}
                      </Typography>
                    )}
                  </Box>
                </ListEntitiesItem>
              ))
            }
            initialSortBy="id"
            initialDirection={SearchQueryOrderDirection.Desc}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionDependencies;
